/**
 * The on-site composer's CRM mode (crm-chat-draft-sync D1, D5, D6, L2, L3, L8): on a crm account's own
 * CRM conversation the drafts menu holds the conversation's CRM reply draft, which the box opens, saves
 * and sends by hand. This module holds the rules; it knows no React or GraphQL. The hook is
 * useCrmChatDraft, the screen ChatMessageComposer.
 */
import { sameDraftText } from '@src/components/sco/Admin/AiCrm/data/format'

export type ChatCrmFile = {
  storedFileId?: string | null
  title?: string | null
  fullUrl?: string | null
  mimeType?: string | null
  deletedAt?: string | null
}

/** crmChatDraft, as the composer reads it. */
export type ChatCrmContext = {
  threadId?: string | null
  accountUserId?: string | null
  toolId?: string | null
  inboundCommentId?: string | null
  seenInboundCommentId?: string | null
  claimOwnerUserId?: string | null
  claimOwnerDisplayName?: string | null
  claimOwnedByViewer?: boolean | null
  draft?: {
    commentId?: string | null
    body?: string | null
    storedFiles?: Array<ChatCrmFile | null> | null
  } | null
}

/**
 * CRM mode is on only for the account itself, in the top-level composer. A sco admin who is not the
 * account keeps the plain composer: what they send there is theirs, not the account's.
 */
export function chatCrmModeOn(input: {
  context: ChatCrmContext | null | undefined
  userId: string | null | undefined
  parentCommentId?: string | null
}): boolean {
  const { context, userId } = input
  return Boolean(
    context && userId && !input.parentCommentId && context.toolId && context.accountUserId === userId
  )
}

/**
 * Who else is working the reply, when the box stays locked (L3), else null. The server's rule
 * (assertReplyOwner): someone else holds a fresh claim and a draft is open.
 */
export function chatCrmLockedBy(
  context: ChatCrmContext | null | undefined,
  userId: string | null | undefined
): string | null {
  if (!context || !context.claimOwnerUserId || !context.draft) {
    return null
  }
  if (context.claimOwnedByViewer || context.claimOwnerUserId === userId) {
    return null
  }
  return (context.claimOwnerDisplayName || '').trim() || 'Someone'
}

/** The draft's live files, oldest first, as the server lists them. */
export function liveFiles(files: Array<ChatCrmFile | null> | null | undefined): ChatCrmFile[] {
  return (files || []).filter((file): file is ChatCrmFile => Boolean(file?.storedFileId) && !file?.deletedAt)
}

/** The box as one key autosave compares: the text and the file ids. */
export function draftKey(body: string | null | undefined, fileIds: string[] | null | undefined): string {
  return JSON.stringify({ body: body || '', files: [...(fileIds || [])].filter(Boolean).sort() })
}

export function parseDraftKey(key: string | null | undefined): { body: string; files: string[] } {
  try {
    const parsed = JSON.parse(key || '')
    return {
      body: typeof parsed?.body === 'string' ? parsed.body : '',
      files: Array.isArray(parsed?.files) ? parsed.files.filter((id: unknown) => typeof id === 'string') : [],
    }
  } catch {
    return { body: '', files: [] }
  }
}

/** Two keys read the same: the text as a person reads it, and the same files. */
export function sameDraftKey(left: string | null | undefined, right: string | null | undefined): boolean {
  const a = parseDraftKey(left)
  const b = parseDraftKey(right)
  return (
    sameDraftText(a.body, b.body) &&
    a.files.length === b.files.length &&
    a.files.every((id, i) => id === b.files[i])
  )
}

/** The server's draft as a key. No draft reads as an empty box. */
export function serverDraftKey(context: ChatCrmContext | null | undefined): string {
  const draft = context?.draft
  return draftKey(
    draft?.body || '',
    liveFiles(draft?.storedFiles).map(file => file.storedFileId as string)
  )
}

/**
 * What a re-read does with the server's draft (D6): nothing when it is what this box last saved or
 * adopted; adopt it when the box has not changed since then; otherwise ask (the box has its own edits).
 */
export function serverDraftAction(input: {
  boxKey: string
  savedKey: string | null
  serverKey: string
}): 'none' | 'adopt' | 'conflict' {
  if (input.savedKey != null && sameDraftKey(input.serverKey, input.savedKey)) {
    return 'none'
  }
  if (sameDraftKey(input.boxKey, input.serverKey)) {
    return 'adopt'
  }
  // Nothing saved from this box yet: an empty box takes the draft, typing is never overwritten.
  const baseline = input.savedKey ?? draftKey('', [])
  return sameDraftKey(input.boxKey, baseline) ? 'adopt' : 'conflict'
}
